import { marketPendingOrderList } from "@/apis/market";
import { DialogLocal } from "@/components/Dialog";
import { OrderType, Payments } from "@/constants";
import { PAGE_SIZE } from "@/constants/config";
import { StateReducer, useCreateReducer } from "@/libs/reducer";
import { useUserInfoStore } from "@/stores/userInfo";
import { MarketPendingOrderList } from "@/types/Api/Market/PendingOrder/marketPendingOrderList";
import { toMoney } from "@/utils/tool";
import { useInfiniteScroll, useMount } from "ahooks";
import { AutoCenter, Toast } from "antd-mobile";
import { Action } from "antd-mobile/es/components/popover";
import { useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTradeStore, TradeFilters } from "@/stores/trade";
const sorts: Action[] = [{
  text: "购买数量:高到低",
  key: "amount:descent"
}, {
  text: "购买数量:低到高",
  key: "amount:ascent"
}, {
  text: "信用等级:高到低",
  key: "level:descent"
}, {
  text: "信用等级:低到高",
  key: "level:ascent"
}, {
  text: "成单率:高到低",
  key: "successRatio:descent"
}, {
  text: "成单率:低到高",
  key: "successRatio:ascent"
}];
export interface PageState {
  showSortActions: boolean;
  showFilterPopup: boolean;
  // 列表筛选项
  amount: string;
  paymentType: Payments[];
  isSplit: OrderType[];
  // 列表排序
  sort: "amount:descent" | "amount:ascent" | "level:descent" | "level:ascent" | "successRatio:descent" | "successRatio:ascent" | "default";
}
const pagestate = {
  showSortActions: false,
  showFilterPopup: false,
  amount: "",
  paymentType: [],
  isSplit: [],
  // sort: "amount:descent" as "amount:descent",
  sort: ("default" as "default")
};
export type ActionType = "update-showSortActions" | "update-showFilterPopup" | "update-amount" | "update-paymentType" | "update-isSplit" | "reset_filters" | "update-list";
let cacheFilters: {
  amount?: string;
  paymentType?: Payments[];
  isSplit: OrderType[];
} = {
  amount: "",
  paymentType: [],
  isSplit: []
};
const actions: StateReducer<PageState, ActionType> = (state, action) => {
  switch (action.type) {
    case "update-showSortActions":
      return Object.assign({}, state, {
        showSortActions: action.showSortActions,
        sort: action.sort || state.sort
      });
    case "update-showFilterPopup":
      if (action.showFilterPopup) {
        cacheFilters = {
          amount: state.amount,
          paymentType: state.paymentType,
          isSplit: state.isSplit
        };
        return Object.assign({}, state, {
          showFilterPopup: action.showFilterPopup
        });
      } else {
        return Object.assign({}, state, {
          showFilterPopup: action.showFilterPopup,
          amount: cacheFilters.amount,
          paymentType: cacheFilters.paymentType,
          isSplit: cacheFilters.isSplit
        });
      }
    case "update-amount":
      return Object.assign({}, state, {
        amount: action.amount
      });
    case "update-paymentType":
      return Object.assign({}, state, {
        paymentType: action.paymentType
      });
    case "update-isSplit":
      return Object.assign({}, state, {
        isSplit: action.isSplit
      });
    case "reset_filters":
      return Object.assign({}, state, {
        amount: "",
        paymentType: [],
        isSplit: ""
      });
    default:
      return state;
  }
};
export const useSetup = () => {
  const navigate = useNavigate();
  const scollableRef = useRef(null);
  const [userInfo] = useUserInfoStore()(store => [store.userInfo]);
  const [tradeFilters, setTradeFilters] = useTradeStore(state => [state.tradeFilters, state.setTradeFilters]);
  const cont_states = Object.assign({}, pagestate, tradeFilters); //初始化合并缓存数据
  const [state, dispatch] = useCreateReducer<PageState, ActionType>(actions, cont_states, {});

  /** `const fetchList` 是一个使用 `tradeList` API 从服务器获取挂单列表的函数。它使用组件的当前状态来构造 API
  请求参数，然后调度一个操作以使用获取的订单列表更新组件状态。该函数还会在组件安装时使用“useEffect”挂钩调用一次。 */
  const infiniteList = useInfiniteScroll<{
    list: MarketPendingOrderList["response"]["list"];
    total: number;
  }>(async d => {
    const page = d ? Math.ceil(d.list.length / PAGE_SIZE) + 1 : 1;
    const response = await marketPendingOrderList({
      amount: toMoney(state.amount),
      paymentType: (state.paymentType?.reduce((a, b) => a + b, "") as string),
      isSplit: state.isSplit[0],
      page: page,
      pageSize: PAGE_SIZE
    });
    if (!userInfo.isRealNameAuth) {
      DialogLocal({
        cancelText: "取消",
        content: <AutoCenter>为保障您的资金安全，请实名认证！</AutoCenter>,
        onConfirm() {
          navigate(`/user_centify`);
        }
      });
      return {
        list: response.data?.list ?? [],
        total: response.data?.total ?? 0
      };
    }
    if (!response.ok && response.code === 400) {
      DialogLocal({
        title: "提示",
        content: <AutoCenter>{response.msg}</AutoCenter>,
        confirmText: "确认",
        // cancelText: response.msg === "应用错误"?"":"暂不绑定",
        cancelText: "暂不绑定",
        async onConfirm() {
          navigate("/payment_add/1");
          return true;
        },
        async onCancel() {
          return true;
        }
      });
    }
    setTradeFilters({
      //查询后设置选中缓存
      amount: state.amount,
      paymentType: state.paymentType,
      isSplit: state.isSplit
    });
    return {
      list: response.data?.list ?? [],
      total: response.data?.total ?? 0
    };
  }, {
    manual: true,
    target: scollableRef,
    isNoMore: d => d?.total === 0 || d?.total === d?.list.length
  });
  const resetFilters = useCallback(() => {
    cacheFilters = {
      amount: state.amount,
      paymentType: state.paymentType,
      isSplit: state.isSplit
    };
    dispatch({
      type: "reset_filters"
    });
  }, [state, dispatch]);
  const applyFilters = useCallback(() => {
    if (state.amount && !/^\d+$/.test(state.amount)) return Toast.show("请输入正确数量");
    cacheFilters = {
      amount: state.amount,
      paymentType: state.paymentType,
      isSplit: state.isSplit
    };
    dispatch({
      type: "update-showFilterPopup",
      showFilterPopup: false
    });
    infiniteList.reload();
  }, [state, dispatch, infiniteList]);
  useMount(() => {
    infiniteList.reload();
  });
  return {
    sorts,
    state,
    dispatch,
    resetFilters,
    applyFilters,
    infiniteList,
    navigate,
    userInfo,
    scollableRef
  };
};